import { useCallback, useEffect, useRef, useState } from "react";

export const SLIDE_TRANSIT_AUTOPLAY_MS = 4500;
export const SLIDE_TRANSIT_DURATION_MS = 650;

export type SlideTransitDirection = "rtl" | "ttb";

export function incomingStartTransform(direction: SlideTransitDirection): string {
  return direction === "rtl" ? "translateX(100%)" : "translateY(-100%)";
}

/** Alternating RTL / TTB autoplay used by corridor advertisements. */
export function useSlideTransitCarousel(itemCount: number) {
  const [index, setIndex] = useState(0);
  const [incomingIndex, setIncomingIndex] = useState<number | null>(null);
  const [incomingActive, setIncomingActive] = useState(false);
  const [direction, setDirection] = useState<SlideTransitDirection>("rtl");
  const stepCountRef = useRef(0);
  const pausedRef = useRef(false);
  const animatingRef = useRef(false);
  const indexRef = useRef(0);
  const countRef = useRef(itemCount);

  indexRef.current = index;
  countRef.current = itemCount;

  useEffect(() => {
    if (itemCount > 0 && index >= itemCount) {
      setIndex(0);
    }
  }, [itemCount, index]);

  const advance = useCallback(() => {
    const count = countRef.current;
    if (count < 2 || animatingRef.current || pausedRef.current) return;
    const next = (indexRef.current + 1) % count;
    const nextDirection: SlideTransitDirection = stepCountRef.current % 2 === 0 ? "rtl" : "ttb";
    stepCountRef.current += 1;
    animatingRef.current = true;
    setDirection(nextDirection);
    setIncomingActive(false);
    setIncomingIndex(next);
  }, []);

  useEffect(() => {
    if (incomingIndex == null) return;
    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setIncomingActive(true));
    });
    const done = window.setTimeout(() => {
      setIndex(incomingIndex);
      setIncomingIndex(null);
      setIncomingActive(false);
      animatingRef.current = false;
    }, SLIDE_TRANSIT_DURATION_MS);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(done);
    };
  }, [incomingIndex]);

  useEffect(() => {
    if (itemCount < 2) return;
    const id = window.setInterval(advance, SLIDE_TRANSIT_AUTOPLAY_MS);
    return () => window.clearInterval(id);
  }, [itemCount, advance]);

  return {
    index,
    incomingIndex,
    incomingActive,
    direction,
    pause: () => {
      pausedRef.current = true;
    },
    resume: () => {
      pausedRef.current = false;
    },
  };
}
